'use client';
import { type RefObject, type MouseEventHandler, useRef } from 'react';
import useAccessibleBlockLink from 'use-accessible-block-link';

import Image from 'next/image';
import { IconBrandLinkedin, IconBrandGithub, IconBrandTwitter } from '@tabler/icons-react';
import Link from 'next/link';

interface MemberProps {
  name: string;
  position: string;
  image: string;
  linkedIn: string;
  twitter: string;
  github: string;
  background: string;
}

export const Member = (props: MemberProps) => {
  const mainEvent = useRef();

  const { handleClick } = useAccessibleBlockLink(mainEvent);

  const [firstName, lastName] = props.name.split(' ');

  return (
    <section
      onClick={handleClick as unknown as MouseEventHandler<HTMLDivElement>}
      // @ts-expect-error @TODO: got to figure out how the hook handles target blank
      target="_blank"
      key={props.name}
      className="TeamMember group relative h-full cursor-pointer brightness-90 transition-[transform,filter] focus-within:-translate-y-1 hover:-translate-y-1"
    >
      <div className="group-hover:border-primary-600 group-focus-within:border-primary-600 transform-[border-color] relative h-96 overflow-clip rounded-tr-2xl border-2 border-gray-300 dark:border-gray-700">
        <div className="absolute left-0 top-0 z-[1] flex items-center space-x-2 p-2 opacity-0 transition-[opacity] group-focus-within:opacity-100 group-hover:opacity-100">
          {props.linkedIn && (
            <Link
              ref={mainEvent as unknown as RefObject<HTMLAnchorElement>}
              href={`https://www.linkedin.com/in/${props.linkedIn}`}
              target="__blank"
              aria-label={`${props.name}'s Linked In (opens in a new window)`}
              className="group-hover:text-primary-700 group-focus-within:text-primary-700 block rounded-md bg-black p-[2px] transition-[color] [.TeamMember:focus-within_&:has(~:focus)]:text-white [.TeamMember:hover_&:has(~:hover)]:text-white"
            >
              <IconBrandLinkedin aria-hidden="true" role="img" />
            </Link>
          )}
          {props.twitter && (
            <Link
              href={`https://twitter.com/${props.twitter}`}
              target="__blank"
              aria-label={`${props.name}'s Twitter (opens in a new window)`}
              className="hover:text-primary-700 focus:text-primary-700 block rounded-md bg-black p-[2px]"
            >
              <IconBrandTwitter aria-hidden="true" role="img" />
            </Link>
          )}
          {/*Lin suggested shutting off, keeping for now in case we can sell her on it!*/}
          {false && props.github && (
            <Link
              href={`https://github.com/${props.github}`}
              target="__blank"
              aria-label={`${props.name}'s Github (opens in a new window)`}
              className="hover:text-primary-700 focus:text-primary-700 block rounded-md bg-black p-[2px]"
            >
              <IconBrandGithub aria-hidden="true" role="img" />
            </Link>
          )}
        </div>
        <Image src={`/images/team/${props.image}.jpg`} alt={props.name} fill className="object-cover object-center" />
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4 text-white backdrop-blur-md">
          <div className="flex justify-between gap-5">
            <h3 className="text-2xl uppercase tracking-tight">
              <span className="block leading-5">{firstName}</span>
              {lastName}
            </h3>
            <h4 className="ml-auto w-min text-sm uppercase leading-4 tracking-tight">{props.position}</h4>
          </div>
          <p className="text-xs text-gray-200">previously {props.background}</p>
        </div>
      </div>
    </section>
  );
};
